import { useEffect, useState } from "react";
import { nativeBridge } from "./bridge.ts";

/**
 * Whether this phone is on Wi-Fi.
 *
 * For the things that must not spend somebody's data on their behalf: a clip that starts itself
 * is the clearest case, because nobody asked for it and a trip's worth of them is a bill. On a
 * mobile network the picture waits for a tap instead, which costs one clip and only the one.
 *
 * In a browser there is nothing to ask — `navigator.connection` is not told the truth by most of
 * them — so a browser counts as free, unless the reader has asked for less data, which is the one
 * answer it does give.
 */
export function useOnWifi(): boolean {
  const bridge = nativeBridge();
  const [wifi, setWifi] = useState(() => !bridge && !saveData());

  useEffect(() => {
    if (!bridge) return;
    let living = true;
    const off = bridge.on("network.changed", (n) => setWifi(n.route === "WIFI"));
    // The event only says what changed, so the first answer has to be asked for. An older shell
    // that cannot answer leaves it as it was: nothing starts itself, which is the safe way round.
    bridge.call("device.network", {}).then(
      (n) => {
        if (living) setWifi(n.route === "WIFI");
      },
      () => undefined,
    );
    return () => {
      living = false;
      off();
    };
  }, [bridge]);

  return wifi;
}

const saveData = () =>
  (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;
